/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import { StateCreator } from 'zustand';
import { Dispatch } from 'react';
import { produce } from 'immer';
import { createAppStore } from '@marriott/mi-store-utils';

// Import types from the HQV reducer
import { hqvInitialStateType, HQV_ACTIONS, hqvInitialState } from '../../reducers/HQV';
import { propertySessionDataType } from '../../context/SessionUpdateContext/index.types';

/**
 * Interface for the HQV Context type
 * This matches the original context structure for seamless migration
 */
export interface HQVContextType {
  hqvState: hqvInitialStateType;
  hqvDispatch: Dispatch<{ type: HQV_ACTIONS; payload: any }>;
}

/**
 * Interface for the HQV Zustand store
 * Matches HQVContextType exactly to maintain API compatibility
 * Plus adds direct methods for each HQV action
 */
export interface HQVStore extends HQVContextType {
  // State - matches the context structure
  hqvState: hqvInitialStateType;

  // Actions - dispatch function that matches the reducer pattern
  hqvDispatch: Dispatch<{ type: HQV_ACTIONS; payload: any }>;

  // Individual setter methods for independent store updates
  setHQVState: (hqvState: hqvInitialStateType) => void;

  // Direct methods for each HQV_ACTIONS (replacing reducer pattern)
  setAmenetiesAvailable: (available: boolean) => void;
  setPhotosAvailable: (available: boolean) => void;
  setPropertyDetailsAvailable: (available: boolean) => void;
  setInformationAvailable: (available: boolean) => void;
  setPropertyID: (propertyID: string) => void;
  setModalOpenState: (isOpen: boolean) => void;
  setOpenGalleryOnLoad: (shouldOpen: boolean) => void;
  setPropertySessionData: (sessionData: propertySessionDataType) => void;
  setPropertyDistance: (distance: string) => void;
  setCachedPropertyMedia: (media: any) => void;

  // Store initialization method (following searchFilterStore pattern)
  hqvInitialization: (initialState?: Partial<hqvInitialStateType>) => void;

  // Store management methods
  resetStore: () => void;
}

/**
 * Initial state for the HQV store
 */
const createInitialState = (): Omit<
  HQVStore,
  | 'hqvDispatch'
  | 'setHQVState'
  | 'hqvInitialization'
  | 'resetStore'
  | 'setAmenetiesAvailable'
  | 'setPhotosAvailable'
  | 'setPropertyDetailsAvailable'
  | 'setInformationAvailable'
  | 'setPropertyID'
  | 'setModalOpenState'
  | 'setOpenGalleryOnLoad'
  | 'setPropertySessionData'
  | 'setPropertyDistance'
  | 'setCachedPropertyMedia'
> => ({
  hqvState: { ...hqvInitialState },
});

/**
 * Reducer function that handles HQV actions
 * Uses the existing hqvReducer logic
 */
const hqvReducerLogic = (
  state: hqvInitialStateType,
  action: { type: HQV_ACTIONS; payload: any }
): hqvInitialStateType => {
  switch (action.type) {
    case HQV_ACTIONS.AMENETIES_AVAILABLE: {
      return { ...state, amenetiesAvailable: action.payload };
    }
    case HQV_ACTIONS.PHOTOS_AVAILABLE: {
      return { ...state, photosAvailable: action.payload };
    }
    case HQV_ACTIONS.PROPERTY_DETAILS_AVAILABLE: {
      return { ...state, propertyDetailsAbailable: action.payload };
    }
    case HQV_ACTIONS.INFORMATION_AVAILABLE: {
      return { ...state, informationAvailable: action.payload };
    }
    case HQV_ACTIONS.PROPERTY_IDENTITY: {
      return { ...state, propertyIDvalue: action.payload };
    }
    case HQV_ACTIONS.MODAL_OPEN_STATE: {
      return { ...state, modalOpenState: action.payload };
    }
    case HQV_ACTIONS.OPEN_GALLERY_ON_LOAD: {
      return { ...state, openGalleryOnLoad: action.payload };
    }
    case HQV_ACTIONS.PROPERTY_SESSION_DATA: {
      return { ...state, propertySessionData: action.payload };
    }
    case HQV_ACTIONS.PROPERTY_DISTANCE: {
      return { ...state, propertyDistanceValue: action.payload };
    }
    case HQV_ACTIONS.CACHED_PROPERTY_MEDIA: {
      return { ...state, cachedPropertyMedia: action.payload };
    }
    default: {
      return { ...state };
    }
  }
};

/**
 * HQV Zustand store creator
 * Maintains the same API as the original Context for seamless migration
 */
const HQVStoreCreator: StateCreator<HQVStore> = set => {
  return {
    ...createInitialState(),

    // hqvDispatch function that replicates the original reducer logic
    hqvDispatch: (action: { type: HQV_ACTIONS; payload: any }) => {
      set(
        produce((state: HQVStore) => {
          // Apply the reducer logic to update the hqvState
          state.hqvState = hqvReducerLogic(state.hqvState, action);
        })
      );
    },

    // Individual setter for the entire hqvState
    setHQVState: (hqvState: hqvInitialStateType) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState = { ...hqvState };
        })
      );
    },

    // Direct methods for each HQV_ACTIONS (replacing reducer pattern)

    setAmenetiesAvailable: (available: boolean) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.amenetiesAvailable = available;
        })
      );
    },

    setPhotosAvailable: (available: boolean) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.photosAvailable = available;
        })
      );
    },

    setPropertyDetailsAvailable: (available: boolean) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.propertyDetailsAbailable = available;
        })
      );
    },

    setInformationAvailable: (available: boolean) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.informationAvailable = available;
        })
      );
    },

    setPropertyID: (propertyID: string) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.propertyIDvalue = propertyID;
        })
      );
    },

    setModalOpenState: (isOpen: boolean) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.modalOpenState = isOpen;
        })
      );
    },

    setOpenGalleryOnLoad: (shouldOpen: boolean) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.openGalleryOnLoad = shouldOpen;
        })
      );
    },

    setPropertySessionData: (sessionData: propertySessionDataType) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.propertySessionData = sessionData;
        })
      );
    },

    setPropertyDistance: (distance: string) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.propertyDistanceValue = distance;
        })
      );
    },

    setCachedPropertyMedia: (media: any) => {
      set(
        produce((state: HQVStore) => {
          state.hqvState.cachedPropertyMedia = media;
        })
      );
    },

    // Store initialization method (following searchFilterStore pattern)
    hqvInitialization: (initialState?: Partial<hqvInitialStateType>) => {
      set(
        produce((state: HQVStore) => {
          if (initialState) {
            // Deep copy the initial state to ensure new references
            const newState = {
              ...hqvInitialState,
              ...initialState,
            };

            // Deep copy propertySessionData if provided
            if (initialState.propertySessionData) {
              newState.propertySessionData = { ...initialState.propertySessionData };
            }

            state.hqvState = newState;
          } else {
            // Reset to default values
            state.hqvState = { ...hqvInitialState };
          }
        })
      );
    },

    resetStore: () => {
      set(
        produce((state: HQVStore) => {
          const initialState = createInitialState();
          Object.assign(state, initialState);
        })
      );
    },
  };
};

/**
 * Create the HQV store instance
 * Global singleton pattern as requested
 */
export const createHQVStore = () => {
  return createAppStore(HQVStoreCreator, {
    usePersistentStore: false,
  });
};

/**
 * Default store instance - single global instance (no componentId registry)
 */
export const useHQVStore = createHQVStore();
